import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import { useId, useState } from "react";

import { ArrowIcon } from "@/components/marketing/MarketingIcons";
import {
  marketingHref,
  newTabProps,
} from "@/components/marketing/marketingLinks";
import {
  PriceTable,
  SpecStrip,
  TableIntro,
  TableTail,
  type PriceColumnData,
  type PriceRowData,
} from "@/components/marketing/PricingTable";
import {
  ACCENT,
  ACCENT_DEEP,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import { sectionPad } from "./landingRun";
import { RichRuns } from "./splitText";
import type {
  LandingCta,
  PlanCard,
  PlanRow,
  PlanTab,
} from "@/content/landing/types";
import {
  LandingButton,
  LandingImage,
  LandingSection,
  SectionHeading,
} from "../LandingPrimitives";
import { usePageIdentity } from "../LandingPageContext";

type Plans = Laid<"plans">;

/**
 * The published table's own data, from the page's. The free row is the only one
 * whose control registers rather than buys, and the published table says which
 * by the fill it carries; the destination is what tells them apart here, as it
 * does on the feature pages.
 */
function tableRows(rows: ReadonlyArray<PlanRow>): ReadonlyArray<PriceRowData> {
  return rows.map((row) => ({
    cells: row.cells,
    badge: row.badge,
    cta: row.cta,
    filled: Boolean(row.cta?.href?.includes("/register")),
  }));
}

/** The column titles, with the shorter first one the published header swaps in below 992. */
function tableColumns(
  columns: ReadonlyArray<string>,
  narrow?: string
): ReadonlyArray<PriceColumnData> {
  return columns.map((label, index) => ({
    label,
    narrow: index === 0 ? narrow : undefined,
  }));
}

/** `filter_pricing_wrap` and its sibling, one trough per group of controls. */
const TROUGH = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  p: "8px",
  borderRadius: "16px",
  backgroundColor: "rgba(233, 238, 241, 0.6)",
  [TINY]: { width: 1 },
};

const PILL = {
  position: "relative",
  px: "16px",
  py: "10.08px",
  gap: "10.08px",
  borderRadius: "10.08px",
  border: `1px solid ${marketingPalette.pillRule}`,
  fontSize: 14.08,
  lineHeight: "21.12px",
  [NOT_WIDE]: { borderColor: "rgba(79, 92, 101, 0.2)" },
  [TINY]: { width: 1, fontSize: 16, lineHeight: "24px" },
};

const PILL_SELECTED = {
  backgroundColor: "common.white",
  border: "1px solid rgba(4, 30, 57, 0.2)",
  fontWeight: 500,
  boxShadow: "rgb(187, 199, 206) 0px 4px 11px -5px",
  // Restated under the step: the base pill's own narrow-step border colour is
  // emitted after the flat rule above.
  [NOT_WIDE]: { borderColor: "rgba(4, 30, 57, 0.2)" },
};

/** The product control holds the gutter the published page draws its icon in. */
const PILL_PRODUCT = { pl: "48px", [TINY]: { py: "11.2px" } };

/**
 * `filter_pricing_icon`. Chrome rather than content: an editor never swaps a
 * product glyph, and `upload-marketing-assets` keeps such art in the repo.
 */
const PRODUCT_ICONS: Record<string, string> = {
  "Proxy Server": "/marketing/pricing/icon-proxy-server.svg",
  "Static Residential": "/marketing/pricing/icon-static-residential.svg",
  "Rotating Residential": "/marketing/pricing/icon-rotating-residential.svg",
};

const PILL_ICON = {
  position: "absolute",
  left: "16px",
  // The pill beside it is positioned too, and paints later: without this the
  // selected one's white fill covers the glyph.
  zIndex: 1,
  width: "22px",
  height: "22px",
};

const TAB_BADGE = {
  position: "absolute",
  top: "-19px",
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  zIndex: 1,
  [TINY]: { top: "-10px" },
};

const TAB_BADGE_PILL = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
  px: "6.4px",
  borderRadius: "100vw",
  backgroundColor: marketingPalette.savePanel,
  color: marketingPalette.saveInk,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "22px",
  [TINY]: { lineHeight: "19.2px" },
};

/**
 * The radio itself, off screen but still focusable and still announced: the
 * pill beside it is what the page draws, and the label around both is the hit
 * area. The published control is built the same way, on a radio group per row.
 */
const CONTROL_INPUT = {
  position: "absolute",
  width: "1px",
  height: "1px",
  m: 0,
  opacity: 0,
};

/** One product or period control of the pricing band's filter. */
function Control({
  tab,
  kind,
  name,
  selected,
  onSelect,
}: {
  tab: PlanTab;
  kind: "product" | "period";
  name: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const icon = kind === "product" ? PRODUCT_ICONS[tab.label] : undefined;
  // `save_label_wrap`, the use-case pages' "Best for" badge on a product, sets
  // it at the start of the control on a phone; every other badge stays centred.
  const { group } = usePageIdentity();
  const badgeStart = kind === "product" && group === "useCase";
  return (
    <Box
      component="label"
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        // The hidden input carries the focus, so the pill has to show it.
        "& input:focus-visible + *": {
          outline: `2px solid ${ACCENT}`,
          outlineOffset: "2px",
        },
        [TINY]: { width: 1 },
      }}
    >
      <Box
        component="input"
        type="radio"
        name={name}
        checked={selected}
        onChange={onSelect}
        sx={CONTROL_INPUT}
      />
      {icon ? <LandingImage src={icon} sx={PILL_ICON} /> : null}
      <Box
        sx={[
          PILL,
          kind === "product" ? PILL_PRODUCT : {},
          selected ? PILL_SELECTED : {},
        ]}
      >
        {tab.label}
      </Box>
      {tab.badge ? <TabBadge label={tab.badge} start={badgeStart} /> : null}
      {tab.discount ? <TabBadge label={tab.discount} discount /> : null}
    </Box>
  );
}

/**
 * The badge the published filter floats over one control of a row: `save_label`
 * in blue, or `discount_label` in the red of the cards' corner pill.
 */
function TabBadge({
  label,
  discount,
  start,
}: {
  label: string;
  discount?: boolean;
  start?: boolean;
}) {
  return (
    <Box
      sx={[
        TAB_BADGE,
        start ? { [TINY]: { justifyContent: "flex-start" } } : null,
      ]}
    >
      <Box
        component="span"
        sx={[
          TAB_BADGE_PILL,
          discount
            ? {
                backgroundColor: PLAN_OFFER.backgroundColor,
                color: PLAN_OFFER.color,
              }
            : null,
        ]}
      >
        {label}
      </Box>
    </Box>
  );
}

/**
 * `form_pricing_filters_wrap`: two troughs side by side while the row holds
 * them, stacking products above periods, the reverse of their written order.
 */
function FilterRow({
  periods,
  tabs,
  period,
  tab,
  onPeriod,
  onTab,
  inHead,
}: {
  periods?: ReadonlyArray<PlanTab>;
  tabs?: ReadonlyArray<PlanTab>;
  period: string;
  tab: string;
  onPeriod: (label: string) => void;
  onTab: (label: string) => void;
  /**
   * Published inside the heading's wrap, whose 12 comes before the row's own
   * margin and whose centred text the controls take.
   */
  inHead?: boolean;
}) {
  // A name per instance: two pricing bands on one page would otherwise share a
  // radio group and steal each other's selection.
  const group = useId();
  return (
    <Box
      sx={[
        {
          display: "flex",
          alignItems: "center",
          gap: "12px",
          mt: "48px",
          textAlign: "start",
          [NOT_WIDE]: { flexDirection: "column-reverse" },
          [MID]: { mt: "16px" },
          [TINY]: { gap: "16px", width: 1 },
        },
        inHead ? { pt: "12px", textAlign: "center" } : null,
      ]}
    >
      {periods?.length ? (
        <Box
          role="radiogroup"
          aria-label="Billing period"
          sx={[TROUGH, { [TINY]: { gap: "16px", textAlign: "center" } }]}
        >
          {periods.map((item) => (
            <Control
              key={item.label}
              tab={item}
              kind="period"
              name={`${group}-period`}
              selected={item.label === period}
              onSelect={() => onPeriod(item.label)}
            />
          ))}
        </Box>
      ) : null}
      {tabs?.length ? (
        <Box
          role="radiogroup"
          aria-label="Proxy product"
          // Below 768 the published trough opens its gap to 12 and centres
          // what wraps, which is what folds the third control under the
          // first two once the column is narrower than the three in a row.
          sx={[
            TROUGH,
            {
              flexWrap: "wrap",
              [MID]: { gap: "12px", justifyContent: "center" },
              [TINY]: { gap: "8px" },
            },
          ]}
        >
          {tabs.map((item) => (
            <Control
              key={item.label}
              tab={item}
              kind="product"
              name={`${group}-product`}
              selected={item.label === tab}
              onSelect={() => onTab(item.label)}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * The card set one filter state shows. Cards naming neither control are drawn
 * in every state: that is what a band with no filter above it carries.
 */
function cardsFor(
  cards: ReadonlyArray<PlanCard> | undefined,
  tab: string,
  period: string
): ReadonlyArray<PlanCard> {
  return (cards ?? []).filter(
    (card) =>
      (card.tab === undefined || card.tab === tab) &&
      (card.period === undefined || card.period === period)
  );
}

/**
 * `.cta_pricing_link`: the card grid's own last item. It spans the whole row
 * above 992 and a fixed count of tracks below, one fewer per step, which is
 * one less than the grid holds at 768 and 480 and one more than it holds
 * between 768 and 992, where it shares its row with the fifth card.
 */
const CARD_FOOTER = {
  gridColumn: "1 / -1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  mt: "12px",
  [NOT_WIDE]: { gridColumn: "span 3" },
  // The published row stands its line above the link once the pair no longer
  // fits beside each other.
  [MID]: { gridColumn: "span 2", flexDirection: "column" },
  [TINY]: { gridColumn: "span 1" },
} satisfies SxProps<Theme>;

/** The line the published footer sets before its link, in the page's own body size. */
const CARD_FOOTER_LEAD = {
  color: marketingPalette.ink,
  fontSize: 16,
  lineHeight: "24px",
} satisfies SxProps<Theme>;

/**
 * `.pricing_link_button`: a quiet accent link over the cards, which becomes a
 * tinted bar below 992 and loses its arrow with the last of its room. Under
 * the pointer it takes that tint at every width, and the accent's deep step.
 */
const CARD_FOOTER_LINK = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  columnGap: "8px",
  height: "41.6px",
  px: "12.8px",
  borderRadius: "8px",
  color: ACCENT,
  fontSize: 16,
  lineHeight: "24px",
  textDecoration: "none",
  transition: "all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)",
  "&:hover": {
    backgroundColor: "rgba(26, 179, 148, 0.1)",
    color: ACCENT_DEEP,
  },
  [NOT_WIDE]: {
    height: "48px",
    px: "16px",
    backgroundColor: "rgba(26, 179, 148, 0.1)",
  },
  [TINY]: { width: 1, px: "8px" },
} satisfies SxProps<Theme>;

/** The arrow the published link drops at the narrowest step. */
const CARD_FOOTER_ARROW = { display: "flex", [TINY]: { display: "none" } };

/**
 * The `pricing_main_item` grid. `auto-fit` rather than a count per breakpoint:
 * the published grid fits as many 230px tracks as the row holds.
 */
function PlanCards({
  cards,
  lead,
  footers,
  outlined,
  centred,
}: {
  cards: ReadonlyArray<PlanCard>;
  /** Every control an outline, the set-apart cards' too. */
  outlined?: boolean;
  /** Inside the heading's wrap, the cards take its centred text. */
  centred?: boolean;
  /** "Can’t find the plan you want?", where the published footer sets one. */
  lead?: string;
  /**
   * The link each product control closes the grid on. The published grid
   * holds every control's link and shows the selected one, so each is drawn
   * and the rest are hidden rather than left out.
   */
  footers?: ReadonlyArray<{ cta: LandingCta; current: boolean }>;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
        // Three declared rows, usually two of them empty: the published grid
        // still counts their gaps.
        gridTemplateRows: "repeat(3, auto)",
        gap: "8px",
        width: 1,
        mt: "32px",
        textAlign: "start",
        [TINY]: { gap: "16px" },
      }}
    >
      {cards.map((card, index) => (
        <PlanCardItem
          // Two cards can carry one title (a monthly and a yearly tier, or
          // the cards of two products left untagged), and the list is static.
          key={`${index}-${card.title}`}
          card={card}
          outlined={outlined}
          centred={centred}
        />
      ))}
      {footers?.map(({ cta, current }, index) => (
        // Not the `hidden` attribute: the row's own `display` would beat it.
        <Box
          key={index}
          sx={[CARD_FOOTER, current ? null : { display: "none" }]}
        >
          {lead ? <Box sx={CARD_FOOTER_LEAD}>{lead}</Box> : null}
          <Box
            component="a"
            href={marketingHref(cta.href ?? "")}
            {...newTabProps(cta)}
            sx={CARD_FOOTER_LINK}
          >
            {cta.label}
            <Box sx={CARD_FOOTER_ARROW}>
              <ArrowIcon />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** The unit price and the plan price, each with what was crossed out beside it. */
function PlanPricing({ card }: { card: PlanCard }) {
  return (
    <>
      {card.unitPrice ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-start",
            gap: "4px",
          }}
        >
          {card.wasUnitPrice ? (
            <Box sx={PLAN_WAS_UNIT_PRICE}>{card.wasUnitPrice}</Box>
          ) : null}
          <Box sx={PLAN_UNIT_PRICE}>{card.unitPrice}</Box>
          {card.per ? (
            <Box sx={{ fontSize: 16, lineHeight: "24px" }}>{card.per}</Box>
          ) : null}
        </Box>
      ) : null}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Box sx={PLAN_PRICE}>{card.price}</Box>
          {card.was ? <Box sx={PLAN_WAS}>{card.was}</Box> : null}
        </Box>
        {card.note ? <Box sx={PLAN_NOTE}>{card.note}</Box> : null}
      </Box>
    </>
  );
}

function PlanCardItem({
  card,
  outlined,
  centred,
}: {
  card: PlanCard;
  outlined?: boolean;
  centred?: boolean;
}) {
  // `is-highlighted` draws the darker rule, and a labelled card carries the
  // filled control. The two are set card by card: the published yearly free
  // card is labelled and filled on the plain rule.
  const setApart = Boolean(card.highlighted);
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "24px",
        p: "22.4px 16px 16px",
        textAlign: centred ? "center" : "start",
        borderRadius: "16px",
        border: `1px solid ${setApart ? "rgb(79, 92, 101)" : "rgba(79, 92, 101, 0.2)"}`,
        backgroundColor: "common.white",
        boxShadow: "rgb(228, 233, 236) 0px 10px 34px -10px",
        [TINY]: { gap: "12px" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "24px",
        }}
      >
        <Box sx={PLAN_TITLE}>{card.title}</Box>
        {card.discount ? <Box sx={PLAN_DISCOUNT}>{card.discount}</Box> : null}
        {card.promo ? <Box sx={PLAN_PROMO}>{card.promo}</Box> : null}
        <PlanPricing card={card} />
      </Box>
      <PlanCta cta={card.cta} filled={Boolean(card.flag) && !outlined} />
      {card.flag ? <Box sx={PLAN_RIBBON}>{card.flag}</Box> : null}
      {card.discountFlag ? (
        <Box sx={PLAN_OFFER}>{card.discountFlag}</Box>
      ) : null}
    </Box>
  );
}

/**
 * The card's own button, not the page's: it fills the card's inner width, and
 * the labelled card's is a shorter filled control rather than an outline.
 * `.pricing_button` brightens on hover and opens its left padding to 24, which
 * slides the label 4 right; `.pricing_button_secondary` only takes a tint, its
 * own later rule holding the padding at 16.
 */
function PlanCta({ cta, filled }: { cta: LandingCta; filled: boolean }) {
  return (
    <Box
      component="a"
      href={marketingHref(cta.href ?? "")}
      {...newTabProps(cta)}
      sx={[
        {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          columnGap: "8px",
          width: 1,
          maxWidth: "100%",
          px: "16px",
          borderRadius: "8px",
          textDecoration: "none",
          transition: "padding-left 0.2s",
        },
        filled
          ? {
              height: "40px",
              backgroundColor: ACCENT,
              color: "common.white",
              fontSize: 14,
              lineHeight: "21px",
              "&:hover": {
                backgroundColor: marketingPalette.accentHover,
                pl: "24px",
              },
            }
          : {
              height: "48px",
              border: `1.5px solid ${ACCENT}`,
              color: marketingPalette.ink,
              fontSize: 16,
              lineHeight: "24px",
              "&:hover": { backgroundColor: "rgba(26, 179, 148, 0.14)" },
            },
      ]}
    >
      {cta.label}
      <ArrowIcon />
    </Box>
  );
}

/** The plan card's type, which does not follow the page's body scale. */
const PLAN_TITLE = { fontSize: 20, fontWeight: 400, lineHeight: "26.08px" };
const PLAN_UNIT_PRICE = { fontSize: 32, fontWeight: 500, lineHeight: "36px" };
const PLAN_PRICE = { fontSize: 16, fontWeight: 500, lineHeight: "20px" };
const PLAN_WAS = {
  fontSize: 12.96,
  fontWeight: 400,
  lineHeight: "20px",
  color: marketingPalette.ink,
  textDecoration: "line-through",
};
const PLAN_WAS_UNIT_PRICE = {
  pr: "3.2px",
  fontSize: 16,
  lineHeight: "24px",
  color: marketingPalette.inkSubtle,
  textDecoration: "line-through",
};
const PLAN_NOTE = {
  fontSize: 12,
  lineHeight: "20px",
  color: "rgba(79, 92, 101, 0.7)",
};
const PLAN_DISCOUNT = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "6.4px",
  px: "8px",
  py: "4px",
  borderRadius: "8px",
  backgroundColor: marketingPalette.discountTint,
  color: marketingPalette.discountInk,
  fontSize: 11.04,
  fontWeight: 500,
  lineHeight: "16px",
  textTransform: "uppercase",
};
/**
 * `pricing-disc-promo-lbl_wrap`: the campaign cards' offer chip. Its dark red
 * is the partner offer's own and occurs nowhere else.
 */
const PLAN_PROMO = {
  px: "8px",
  py: "4px",
  borderRadius: "8px",
  backgroundColor: "rgba(164, 16, 16, 0.08)",
  color: "#9D0A0A",
  fontSize: 10.08,
  fontWeight: 500,
  lineHeight: "13.104px",
  textTransform: "uppercase",
};

/**
 * `discount-label_wrap`: the rotating cards' corner flag. Its red is the site's
 * only one, so it is stated here rather than added to the shared palette.
 */
const PLAN_OFFER = {
  position: "absolute",
  top: "24px",
  right: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: "6.4px",
  borderRadius: "100vw",
  backgroundColor: "#F8DCDC",
  color: "#DA2424",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "22px",
};

/** `pricing-lbl`, which hangs off the top edge rather than sitting inside it. */
const PLAN_RIBBON = {
  position: "absolute",
  top: "-14px",
  left: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: "16px",
  py: "4px",
  borderRadius: "8px",
  backgroundColor: marketingPalette.darkGround,
  color: "common.white",
  fontSize: 11.04,
  fontWeight: 600,
  lineHeight: "20px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};

/**
 * `pricing_bg_wrap`: the tinted band the card grid's top half sits on, from
 * the section's top edge.
 */
const PLAN_BAND = {
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  height: "450px",
  borderRadius: "0px 0px 32px 32px",
  backgroundColor: marketingPalette.surface,
  zIndex: -1,
};

/** `.table-pricing_wrapper`, the column every part of the band stacks in. */
const BAND_COLUMN = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-end",
} satisfies SxProps<Theme>;

/** The same column on a page that prints a table, which sets its own rhythm below 768. */
const TABLE_BAND_COLUMN = {
  ...BAND_COLUMN,
  [MID]: { gap: "16px", alignItems: "stretch" },
} satisfies SxProps<Theme>;

/** The IP-count chip a premium band prints under its heading. */
const PLAN_CHIP = {
  mt: "16px",
  px: "12px",
  py: "6px",
  borderRadius: "8px",
  backgroundColor: marketingPalette.premiumPanel,
  fontSize: 13,
} satisfies SxProps<Theme>;

/**
 * The published column ends in an empty embed under its table and, below 768,
 * counts a gap for it; most pages then stand the column and the band in a
 * second column, which counts another. So the bare band's own 24 grows by 32,
 * or by 16 on a page that leaves the second column out, and the headed band,
 * which drops its own margin there, takes the gaps alone.
 */
const TAIL_MARGIN = { [MID]: { mt: "56px" } };
const TAIL_MARGIN_BARE = { [MID]: { mt: "40px" } };
const CALLOUT_MARGIN = { [MID]: { mt: "32px" } };
const CALLOUT_MARGIN_BARE = { [MID]: { mt: "16px" } };

/**
 * The published section's own ids, which the pages' "See Pricing" controls
 * and the alternatives' "Check Full Pricing" links point at: one on the
 * table pages, another on the card grid the comparison pages draw.
 */
const TABLE_ID = "pricing-list";
const CARDS_ID = "Competitor-Pricing";

/**
 * `.container_new.padding-bottom`, the short close the feature pages name
 * `break`: `LandingSection` has no key for it, so it is spelt out here.
 */
const BREAK_PADDING = {
  pb: "48px",
  [NOT_WIDE]: { pb: "64px" },
  [TINY]: { pb: "48px" },
} satisfies SxProps<Theme>;

/** The section the band stands in: its rhythm from `sectionLayout`, its id by what it holds. */
function planSection(block: Plans, table: boolean) {
  return {
    padding: block.pad === "break" ? ("none" as const) : sectionPad(block.pad),
    // `.pricing_plan_wrap.is-use-case`: the wrap opens 64 under the section.
    sx: [
      block.pad === "break" ? BREAK_PADDING : null,
      block.planLead === "wide"
        ? { pt: "128px", [NOT_WIDE]: { pt: "80px" } }
        : block.planLead
          ? { pt: "64px" }
          : null,
    ],
    id: block.anchor ?? (table ? TABLE_ID : CARDS_ID),
  };
}

/** The line under the cards is the band's; the link after it is each product's own. */
function productLinks(block: Plans, tab: string) {
  return block.tabs?.flatMap((item) =>
    item.cta ? [{ cta: item.cta, current: item.label === tab }] : []
  );
}

/** What the band opens on: the table's intro on its measure, or the shared heading, and the chip. */
function PlanIntro({ block, table }: { block: Plans; table: boolean }) {
  return (
    <>
      {table ? (
        <TableIntro
          title={block.title}
          body={block.body}
          wide={block.wideIntro}
        />
      ) : (
        <SectionHeading
          bodyTone={block.bodyTone}
          title={block.title}
          body={block.body}
          align={block.headingAlign ?? "center"}
        />
      )}
      {block.chip ? <Box sx={PLAN_CHIP}>{block.chip}</Box> : null}
    </>
  );
}

/** The filter row and the spec strip, where the block carries them. */
function PlanControls({
  block,
  tab,
  period,
  onTab,
  onPeriod,
}: {
  block: Plans;
  tab: string;
  period: string;
  onTab: (tab: string) => void;
  onPeriod: (period: string) => void;
}) {
  return (
    <>
      {block.periods?.length || block.tabs?.length ? (
        <FilterRow
          periods={block.periods}
          tabs={block.tabs}
          period={period}
          tab={tab}
          onPeriod={onPeriod}
          onTab={onTab}
          inHead={block.plansInHead}
        />
      ) : null}
      {block.highlights?.length || block.note ? (
        <SpecStrip items={block.highlights} note={block.note} />
      ) : null}
    </>
  );
}

/** The price table, where the block carries one. */
function PlanTable({ block }: { block: Plans }) {
  const { columns, rows } = block;
  return columns?.length && rows?.length ? (
    <PriceTable
      columns={tableColumns(columns, block.narrowColumn)}
      rows={tableRows(rows)}
    />
  ) : null;
}

/** The cards or the table, and a plain link to the pricing page where the block carries neither. */
function PlanBody({
  block,
  cards,
  footers,
}: {
  block: Plans;
  cards: ReturnType<typeof cardsFor>;
  footers: ReturnType<typeof productLinks>;
}) {
  return (
    <>
      {cards.length ? (
        <PlanCards
          cards={cards}
          lead={block.footer?.lead}
          footers={footers}
          outlined={block.outlineControls}
          centred={block.plansInHead}
        />
      ) : null}
      <PlanTable block={block} />
      {!block.cards?.length && !block.rows?.length ? (
        <Box sx={{ mt: "24px" }}>
          <LandingButton cta={{ label: "See all plans", href: "/pricing" }} />
        </Box>
      ) : null}
    </>
  );
}

/** The tails under the band: the table's own, and the callout the rotating page publishes. */
function PlanTails({ block, table }: { block: Plans; table: boolean }) {
  const { footer } = block;
  return (
    <>
      {table && footer ? (
        <TableTail
          chips={footer.reassurances}
          sx={block.bareTail ? TAIL_MARGIN_BARE : TAIL_MARGIN}
        >
          <LandingButton cta={footer.cta} />
        </TableTail>
      ) : null}
      {block.callout ? (
        <TableTail
          heading={block.callout.title}
          lines={
            // `main_header_text_btn_holder`: one holder, the copy's own breaks
            // and links inside it.
            <RichRuns
              text={block.callout.paragraphs.join("\n")}
              links={block.callout.links}
            />
          }
          sx={block.bareTail ? CALLOUT_MARGIN_BARE : CALLOUT_MARGIN}
        >
          <LandingButton cta={block.callout.cta} />
        </TableTail>
      ) : null}
    </>
  );
}

/**
 * The pricing band: the plan's included limits, the price table and the
 * register button. The figures are page copy from the CMS, not a fetch, so a
 * stale one is a content edit; the band's rhythm is `sectionLayout`'s.
 */
/** The published filter opens on the first control of each row. */
const firstLabel = (items: ReadonlyArray<{ label: string }> | undefined) =>
  items?.[0]?.label ?? "";

/** Whether the block draws the price table rather than cards. */
const hasTable = (block: Plans) =>
  Boolean(block.columns?.length && block.rows?.length);

export default function PlansBlock({ block }: { block: Plans }) {
  const [tab, setTab] = useState(block.initialTab ?? firstLabel(block.tabs));
  const [period, setPeriod] = useState(firstLabel(block.periods));
  const cards = cardsFor(block.cards, tab, period);
  const table = hasTable(block);
  return (
    <LandingSection {...planSection(block, table)}>
      {cards.length && !block.noBand ? <Box sx={PLAN_BAND} /> : null}
      <Box sx={table ? TABLE_BAND_COLUMN : BAND_COLUMN}>
        <PlanIntro block={block} table={table} />
        <PlanControls
          block={block}
          tab={tab}
          period={period}
          onTab={setTab}
          onPeriod={setPeriod}
        />
        <PlanBody
          block={block}
          cards={cards}
          footers={productLinks(block, tab)}
        />
      </Box>
      <PlanTails block={block} table={table} />
    </LandingSection>
  );
}
